import React, { useEffect, useRef, useState, Dispatch, SetStateAction } from "react";
import styles from "./chart.module.scss";
import { Candle, CandleTypePart, DirectionsOfCandle, TimeFrames } from "../../../shared/types";
import { useAppSelector } from "../../../store/store";
import {
  colorOfLegendCanvas,
  colorOfLineSimulatedTransactionCanvas,
  colorOfVolumeOnDojiCandle,
  colorOfVolumeOnDownCandle,
  colorOfVolumeOnUpCandle,
  countCandleOnSimulatedTransactionCanvas,
  formulaOfSimulatedTransactionCandleCanvas,
  heightSimulatedTransactionVolumeCanvas,
  lineWidthOfVolumeCandlestickCanvas,
  lineWidthOfVolumeSimulatedTransactionCanvas,
  offsetChartFromLeftBorderSimulatedTransactionCanvas,
} from "../../../shared/configCanvas";
import {
  delay,
  frameGate,
  getDirectionOfCandle,
  getLimitPrices,
  getStatesOfLastCandleForImitation,
} from "../../../shared/lib/lib";
import {
  buildCandle,
  buildSimulatedTransactionMarks,
  buildVolumeCandle,
  calcColorPositionForProfitBeginAnimation,
  calcOpacityAnScaleForMark,
  calcParamsForProfitEndAnimation,
  calcScaleForProfitBeginAnimation,
  scalePrice,
} from "../../../shared/lib/canvas";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calcProfit } from "../functions";
import { translate } from "../translation";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ICanvasSize {
  width: number;
  height: number;
}

interface Props {
  simulationIsInProcess: boolean;
  greetingEnded: boolean;
  chart: Candle[];
  timeframe: TimeFrames;
  dateTimeOpenOrder: string;
  dateTimeAdd1Order: string | undefined;
  dateTimeAdd2Order: string | undefined;
  dateTimeCloseOrder: string;
  priceOpenOrder: string;
  priceAdd1Order: string | undefined;
  priceAdd2Order: string | undefined;
  priceCloseOrder: string;
  language: string;
  ticker: string;
  exchangeName: string;
  companyName: string;
  accentColor: string;
  transformCanvas: { widthRatio: number; heightRatio: number };
  movieRefTitle: React.RefObject<HTMLDivElement>;
  setHookSecondTitleIsActive: React.Dispatch<React.SetStateAction<Boolean>>;
}

const Chart = ({
  simulationIsInProcess,
  greetingEnded,
  chart,
  timeframe,
  dateTimeOpenOrder,
  dateTimeAdd1Order,
  dateTimeAdd2Order,
  dateTimeCloseOrder,
  priceOpenOrder,
  priceAdd1Order,
  priceAdd2Order,
  priceCloseOrder,
  language,
  ticker,
  exchangeName,
  companyName,
  accentColor,
  transformCanvas,
  movieRefTitle,
  setHookSecondTitleIsActive,
}: Props) => {
  const screenWidth = useAppSelector(state => state.appReduser.screenWidth);

  const chartCanvas = useRef<HTMLCanvasElement>(null);
  const [canvasContext, setCanvasContext] = useState<CanvasRenderingContext2D | null>(null);
  const [canvasSize, setCanvasSize] = useState<ICanvasSize>();
  const [chartFlow, setChartFlow] = useState<Candle[][]>(); //массив графиков, где каждый последующий график это предыдущий с обновленной последней ценой, тоесть если эти графики строить по очереди заменяя один другим, то получится реалтайм движение графика
  const [currentChart, setCurrentChart] = useState<Candle[]>(); //график, из chartFlow, который нужно построить сейчас
  const [profit, setProfit] = useState<string>(""); //доходность открытой позиции
  const [endRecording, setEndRecording] = useState<boolean>(false); //флаг завершения анимации и записи

  const [numberOfOpenCandle, setNumberOfOpenCandle] = useState<number>(); //номер свечи на графике на которой было открытие позиции
  const [numberOfAdd1Candle, setNumberOfAdd1Candle] = useState<number>(); //номер свечи на графике на которой было добавление1 позиции
  const [numberOfAdd2Candle, setNumberOfAdd2Candle] = useState<number>(); //номер свечи на графике на которой было добавление2 позиции
  const [numberOfCloseCandle, setNumberOfCloseCandle] = useState<number>(); //номер свечи на графике на которой было закрытие позиции

  const markOfOpenPositionCountRef = useRef(0);
  const markOfAdd1PositionCountRef = useRef(0);
  const markOfAdd2PositionCountRef = useRef(0);
  const markOfClosePositionCountRef = useRef(0);
  const markOfOpenRef = useRef<HTMLDivElement>(null);
  const markOfAdd1Ref = useRef<HTMLDivElement>(null);
  const markOfAdd2Ref = useRef<HTMLDivElement>(null);
  const markOfCloseRef = useRef<HTMLDivElement>(null);
  const profitRef = useRef<HTMLParagraphElement>(null);

  //расчет размера canvas
  useEffect(() => {
    if (screenWidth && transformCanvas) {
      const width = Math.round(973 * transformCanvas.widthRatio);
      const height = Math.round(1007 * transformCanvas.heightRatio);
      setCanvasSize({
        width: width,
        height: height,
      });
    }
  }, [screenWidth, transformCanvas]);

  //изначальное получение canvas графика и настройка
  useEffect(() => {
    if (chartCanvas.current) {
      const canvas = chartCanvas.current;
      const context = canvas.getContext("2d");
      if (context) {
        //это решение вопроса размытой графики
        context.translate(0.5, 0.5);
        setCanvasContext(context);
      }
    }
  }, [chartCanvas]);

  //рисование графика
  useEffect(() => {
    if (canvasSize && canvasContext && currentChart) {
      //флаги что сделка открыта на фиктивном реалтайм графике
      let isOpen = undefined;
      let isAdd1 = undefined;
      let isAdd2 = undefined;
      let isClose = undefined;

      //очистка холста при перерендере
      canvasContext.clearRect(0, 0, canvasSize.width, canvasSize.height);
      canvasContext.beginPath();
      //константы для графики и различные переменные для смещения и трансформирования размеров графики под размер холста
      const indOfLastCandle = currentChart.length - 1;
      const countCandleOnCanvas = countCandleOnSimulatedTransactionCanvas;
      const colorOfLine = colorOfLineSimulatedTransactionCanvas;
      const scaleXCandle = formulaOfSimulatedTransactionCandleCanvas;
      const { highPrice, lowPrice, highVolume } = getLimitPrices(
        currentChart,
        indOfLastCandle - countCandleOnCanvas + 1,
        indOfLastCandle
      );
      const scaleYChart = scalePrice(
        highPrice,
        lowPrice,
        canvasSize.height - heightSimulatedTransactionVolumeCanvas - 5,
        0
      );
      const scaleYVolume = scalePrice(highVolume, 0, heightSimulatedTransactionVolumeCanvas - 5, 0);
      const startOfScaleY = lowPrice;
      const offsetLeftBorder = offsetChartFromLeftBorderSimulatedTransactionCanvas;
      const stepCandleOnScaleX = formulaOfSimulatedTransactionCandleCanvas.total;
      const colorVolumeOnUpCandle = colorOfVolumeOnUpCandle;
      const colorVolumeOnDownCandle = colorOfVolumeOnDownCandle;
      const colorVolumeOnDojiCandle = colorOfVolumeOnDojiCandle;
      const lineWidthOfVolume = lineWidthOfVolumeSimulatedTransactionCanvas;
      const colorOfLegend = colorOfLegendCanvas;

      //-------рисование графики----------

      //график
      for (let i = indOfLastCandle - countCandleOnCanvas + 1; i <= indOfLastCandle; i++) {
        const newInd = indOfLastCandle - i;
        buildCandle(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          currentChart[i],
          newInd,
          colorOfLine,
          true
        );
      }

      //индикатор Volume
      buildVolumeCandle(
        canvasContext,
        currentChart,
        scaleYVolume,
        canvasSize.height,
        canvasSize.width,
        0,
        0,
        stepCandleOnScaleX,
        offsetLeftBorder,
        colorVolumeOnUpCandle,
        colorVolumeOnDownCandle,
        colorVolumeOnDojiCandle,
        colorOfLegend,
        lineWidthOfVolume
      );

      //метка сделки открытия
      if (
        numberOfOpenCandle &&
        markOfOpenRef.current &&
        currentChart.length - 1 >= numberOfOpenCandle &&
        currentChart[numberOfOpenCandle][CandleTypePart.low] <= parseFloat(priceOpenOrder) &&
        currentChart[numberOfOpenCandle][CandleTypePart.high] >= parseFloat(priceOpenOrder)
      ) {
        if (greetingEnded && markOfOpenPositionCountRef.current < 45) {
          //снимаем видео с FPS 30, т.е. 45 кадров это будет 1.5 сек на полный цикл появления метки
          const { opacity, scale } = calcOpacityAnScaleForMark(markOfOpenPositionCountRef.current);
          markOfOpenRef.current.style.opacity = opacity.toString();
          markOfOpenRef.current.style.transform = `scale(${scale})`;
          markOfOpenPositionCountRef.current++;
        }

        isOpen = priceOpenOrder;
        const newOpenInd = indOfLastCandle - numberOfOpenCandle;
        buildSimulatedTransactionMarks(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          priceOpenOrder,
          newOpenInd,
          colorOfLine,
          DirectionsOfCandle.UP
        );
      }
      //метка сделки добавления1
      if (
        numberOfAdd1Candle &&
        markOfAdd1Ref.current &&
        priceAdd1Order &&
        currentChart.length - 1 >= numberOfAdd1Candle &&
        currentChart[numberOfAdd1Candle][CandleTypePart.low] <= parseFloat(priceAdd1Order) &&
        currentChart[numberOfAdd1Candle][CandleTypePart.high] >= parseFloat(priceAdd1Order)
      ) {
        if (greetingEnded && markOfAdd1PositionCountRef.current < 45) {
          const { opacity, scale } = calcOpacityAnScaleForMark(markOfAdd1PositionCountRef.current);
          markOfAdd1Ref.current.style.opacity = opacity.toString();
          markOfAdd1Ref.current.style.transform = `scale(${scale})`;
          markOfAdd1PositionCountRef.current++;
        }

        isAdd1 = priceAdd1Order;
        const newAdd1Ind = indOfLastCandle - numberOfAdd1Candle;
        buildSimulatedTransactionMarks(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          priceAdd1Order,
          newAdd1Ind,
          colorOfLine,
          DirectionsOfCandle.UP
        );
      }
      //метка сделки добавления2
      if (
        numberOfAdd2Candle &&
        markOfAdd2Ref.current &&
        priceAdd2Order &&
        currentChart.length - 1 >= numberOfAdd2Candle &&
        currentChart[numberOfAdd2Candle][CandleTypePart.low] <= parseFloat(priceAdd2Order) &&
        currentChart[numberOfAdd2Candle][CandleTypePart.high] >= parseFloat(priceAdd2Order)
      ) {
        if (greetingEnded && markOfAdd2PositionCountRef.current < 45) {
          const { opacity, scale } = calcOpacityAnScaleForMark(markOfAdd2PositionCountRef.current);
          markOfAdd2Ref.current.style.opacity = opacity.toString();
          markOfAdd2Ref.current.style.transform = `scale(${scale})`;
          markOfAdd2PositionCountRef.current++;
        }

        isAdd2 = priceAdd2Order;
        const newAdd2Ind = indOfLastCandle - numberOfAdd2Candle;
        buildSimulatedTransactionMarks(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          priceAdd2Order,
          newAdd2Ind,
          colorOfLine,
          DirectionsOfCandle.UP
        );
      }
      //метка сделки закрытия
      if (
        numberOfCloseCandle &&
        markOfCloseRef.current &&
        currentChart.length - 1 >= numberOfCloseCandle &&
        currentChart[numberOfCloseCandle][CandleTypePart.high] >= parseFloat(priceCloseOrder) &&
        currentChart[numberOfCloseCandle][CandleTypePart.low] <= parseFloat(priceCloseOrder)
      ) {
        if (greetingEnded && markOfClosePositionCountRef.current < 45) {
          const { opacity, scale } = calcOpacityAnScaleForMark(markOfClosePositionCountRef.current);
          markOfCloseRef.current.style.opacity = opacity.toString();
          markOfCloseRef.current.style.transform = `scale(${scale})`;
          markOfClosePositionCountRef.current++;
        }

        isClose = priceCloseOrder;
        const newCloseInd = indOfLastCandle - numberOfCloseCandle;
        buildSimulatedTransactionMarks(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          priceCloseOrder,
          newCloseInd,
          colorOfLine,
          DirectionsOfCandle.DOWN
        );
      }

      //расчет доходности
      if (isOpen) {
        let profit = 0;
        let profitStr = "";
        let currentPrice = 0;
        let averagePricePositions = 0;
        let sum = 0;
        let count = 0;
        sum += parseFloat(isOpen);
        count += 1;
        if (isAdd1) {
          sum += parseFloat(isAdd1);
          count += 1;
        }
        if (isAdd2) {
          sum += parseFloat(isAdd2);
          count += 1;
        }
        averagePricePositions = sum / count;
        if (!isClose) {
          currentPrice = currentChart[currentChart.length - 1][CandleTypePart.close];
        } else {
          currentPrice = parseFloat(priceCloseOrder);
        }
        profit = Math.round(((currentPrice - averagePricePositions) / (averagePricePositions / 100)) * 100) / 100;
        if (profit >= 0) {
          profitStr = `+${profit.toFixed(1)}`;
        } else {
          profitStr = `${profit.toFixed(1)}`;
        }
        setProfit(profitStr);
      }
    }
  }, [canvasContext, canvasSize, currentChart, timeframe]);

  //определение свечей на которых было открытие, добавления и закрытие позиции
  useEffect(() => {
    const dtOpen = dateTimeOpenOrder ? dayjs.tz(dateTimeOpenOrder, "America/New_York") : undefined;
    const dtAdd1 = dateTimeAdd1Order ? dayjs.tz(dateTimeAdd1Order, "America/New_York") : undefined;
    const dtAdd2 = dateTimeAdd2Order ? dayjs.tz(dateTimeAdd2Order, "America/New_York") : undefined;
    const dtClose = dateTimeCloseOrder ? dayjs.tz(dateTimeCloseOrder, "America/New_York") : undefined;
    let indOfCandleWhereOpenPosition = undefined;
    let indOfCandleWhereAdd1Position = undefined;
    let indOfCandleWhereAdd2Position = undefined;
    let indOfCandleWhereClosePosition = undefined;

    chart.forEach((candle, index) => {
      const dtOpenCandle = dayjs.tz(candle[CandleTypePart.begin], "America/New_York");
      if (dtOpen && dtOpenCandle <= dtOpen) {
        indOfCandleWhereOpenPosition = index;
      }
      if (dtAdd1 && dtOpenCandle <= dtAdd1) {
        indOfCandleWhereAdd1Position = index;
      }
      if (dtAdd2 && dtOpenCandle <= dtAdd2) {
        indOfCandleWhereAdd2Position = index;
      }
      if (dtClose && dtOpenCandle <= dtClose) {
        indOfCandleWhereClosePosition = index;
      }
    });

    setNumberOfOpenCandle(indOfCandleWhereOpenPosition);
    setNumberOfAdd1Candle(indOfCandleWhereAdd1Position);
    setNumberOfAdd2Candle(indOfCandleWhereAdd2Position);
    setNumberOfCloseCandle(indOfCandleWhereClosePosition);
  }, [
    chart,
    dateTimeOpenOrder,
    dateTimeAdd1Order,
    dateTimeAdd2Order,
    dateTimeCloseOrder,
    priceOpenOrder,
    priceAdd1Order,
    priceAdd2Order,
    priceCloseOrder,
  ]);

  //создание массива графиков, по которому будем создавать реалтайм движение графика. Каждый последующий элемент это следующее состояние графика во времени
  useEffect(() => {
    if (chart.length !== 0 && numberOfOpenCandle && numberOfCloseCandle) {
      const chartFlow: Candle[][] = [];
      //создаем текущие состояния графика начиная со свечи где открыта сделка, но немного заранее на две свечи
      const firstCandleIndex = Math.max(numberOfOpenCandle - 4, 1);
      const lastCandleIndex = Math.min(numberOfCloseCandle + 2, chart.length - 1);

      for (let i = firstCandleIndex; i <= lastCandleIndex; i++) {
        //текущий график без последней свечи
        const currentChartWithoutLastCandle = JSON.parse(JSON.stringify(chart)).slice(0, i);
        //последняя свеча
        const lastCandleInCurrentChart = chart[i];
        //направление предыдущей свечи
        const previousCandleDirection = getDirectionOfCandle(chart[i - 1]);
        //состояния последней свечи с течением времени
        const arrStatesOfLastCandle = getStatesOfLastCandleForImitation(
          lastCandleInCurrentChart,
          10,
          previousCandleDirection
        );
        arrStatesOfLastCandle.forEach(currentStateOfCandle => {
          const copyCurrentChartWithoutLastCandle = JSON.parse(JSON.stringify(currentChartWithoutLastCandle));
          copyCurrentChartWithoutLastCandle.push(currentStateOfCandle);
          chartFlow.push(copyCurrentChartWithoutLastCandle);
        });
      }
      setChartFlow(chartFlow);
    }
  }, [chart, numberOfOpenCandle, numberOfCloseCandle]);

  //открываем первичное состояние графика перед началом симуляции
  useEffect(() => {
    if (chartFlow && chartFlow.length !== 0) {
      //находим итоговую доходность
      const profitStr = calcProfit(priceOpenOrder, priceAdd1Order, priceAdd2Order, priceCloseOrder);

      //обнуляю состояние надписи Профит
      if (profitRef.current) {
        profitRef.current.style.transform = `scale(${1.0})`;
        profitRef.current.style.backgroundImage = `linear-gradient(30deg, ${accentColor} ${-100}%, rgba(255, 255, 255, 0.9) ${0}%)`;
        profitRef.current.style.top = `${-60}px`;
        profitRef.current.style.right = `${-1}px`;
        profitRef.current.style.opacity = `${1}`;
        profitRef.current.style.fontSize = `${46}px`;
      }

      //открываем начальное состояние графика и итоговую доходность
      const func = async () => {
        if (!simulationIsInProcess) {
          //если это действительно стартовое состояние, т.е симуляция еще не начать, то показываем результат, для снятия скриншота всей сделки
          setCurrentChart(chartFlow[chartFlow.length - 1]);
        } else {
          //если симуляция начата, то показываем стартовое состояние графика, до наала сделки
          setCurrentChart(chartFlow[0]);
        }
        setProfit(profitStr);
      };
      func();
    }
  }, [chartFlow, simulationIsInProcess]);

  //цикл стартовой анимации надписи Профит
  const profitBeginAnimation = async () => {
    await delay(200);
    //пауза 330ms, которая снимается на видео между вступлением и пульсом профита
    for (let i = 0; i < 10; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }

    //цикл стартовой анимации надписи
    for (let i = 0; i < 34; i++) {
      const scale = calcScaleForProfitBeginAnimation(i);
      const { color1Position, color2Position } = calcColorPositionForProfitBeginAnimation(i);

      if (profitRef.current) {
        profitRef.current.style.transform = `scale(${scale})`;
        profitRef.current.style.backgroundImage = `linear-gradient(30deg, ${accentColor} ${color1Position}%, rgba(255, 255, 255, 0.9) ${color2Position}%)`;
      }
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }

    //пауза 230ms, которая снимается на видео, между пульсом профита и началом бега графика
    for (let i = 0; i < 7; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }
  };

  //цикл финальной анимации надписи Профит
  const profitEndAnimation = async () => {
    await delay(200);
    //пауза 500ms, которая снимается на видео между вступлением и пульсов профита
    for (let i = 0; i < 15; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }

    //цикл стартовой анимации надписи
    for (let i = 0; i < 46; i++) {
      const { top, right, scale, opacity, fontSize } = calcParamsForProfitEndAnimation(i);

      if (profitRef.current) {
        profitRef.current.style.top = `${top}px`;
        profitRef.current.style.right = `${right}px`;
        profitRef.current.style.transform = `scale(${scale})`;
        profitRef.current.style.opacity = `${opacity}`;
        profitRef.current.style.fontSize = `${fontSize}px`;
      }
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }

    //пауза 200ms, которая снимается на видео, между пульсом профита и началом бега графика
    for (let i = 0; i < 6; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }
  };

  //проходим по chartFlow для создания реалтайм движения графика
  useEffect(() => {
    if (chartFlow && chartFlow.length !== 0 && simulationIsInProcess && greetingEnded) {
      //переменные для отображения второго заголовка (его отобразим немного дальше середины хода анимации графика)
      const interval = 33; //fps 30
      const pauseBeforeChangeTitle = Math.ceil(chartFlow.length * interval * 0.6);
      const startHidingTimingFirstTitle = 0 + pauseBeforeChangeTitle;
      const endHidingTimingFirstTitle = 300 + pauseBeforeChangeTitle;
      const startShowingTimingSecondTitle = 500 + pauseBeforeChangeTitle;
      const endShowingTimingSecondTitle = 1000 + pauseBeforeChangeTitle;
      let opacityTitle = 1;
      const durationHidingFirstTitle = endHidingTimingFirstTitle - startHidingTimingFirstTitle;
      const durationShowingSecondTitle = endShowingTimingSecondTitle - startShowingTimingSecondTitle;
      const countIterationHidingFirstTitle = Math.floor(durationHidingFirstTitle / interval);
      const countIterationShowingSecondTitle = Math.floor(durationShowingSecondTitle / interval);
      const opacityIncrementHidingFirstTitle = 1.0 / countIterationHidingFirstTitle;
      const opacityIncrementShowingSecondTitle = 1.0 / countIterationShowingSecondTitle;

      const func = async () => {
        await profitBeginAnimation();
        setProfit("");
        await delay(200);
        for (let i = 0; i < chartFlow.length; i++) {
          //отрисовываю новое состояние графика
          setCurrentChart(chartFlow[i]);

          //в какой-то момент скрываем первый заголовок и проявляем второй
          if (movieRefTitle.current) {
            const timing = i * interval;
            if (timing < startHidingTimingFirstTitle) {
              //ничего с заголовками не делаем
            } else if (timing < endHidingTimingFirstTitle) {
              //скрываем заголовок (чтобы скрыть первый заголовок, он сейчас активен)
              if (timing + interval < endHidingTimingFirstTitle) {
                movieRefTitle.current.style.opacity = opacityTitle.toString();
                opacityTitle -= opacityIncrementHidingFirstTitle;
              } else {
                movieRefTitle.current.style.opacity = "0.0";
                opacityTitle = 0;
              }
            } else if (timing < startShowingTimingSecondTitle) {
              //делаем подмену заголовков (пока он скрыт)
              setHookSecondTitleIsActive(true);
            } else if (timing < endShowingTimingSecondTitle) {
              //проявляем заголовок (чтобы показать второй заголовок, он сейчас активен)
              if (timing + interval < endShowingTimingSecondTitle) {
                movieRefTitle.current.style.opacity = opacityTitle.toString();
                opacityTitle += opacityIncrementShowingSecondTitle;
              } else {
                movieRefTitle.current.style.opacity = "1.0";
                opacityTitle = 1;
              }
            }
          }

          //немного ждем отрисовку канвас и заголовка
          await delay(200);
          //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
          await frameGate();

          //если движение закончено, то подсветим анимацией значение profit
          if (i === chartFlow.length - 1) {
            await delay(200);
            //немного не успевает пропасть метка закрытия за две последних свечи после закрытия, поэтому доскрываю
            for (let i = 0; i < 7; i++) {
              if (markOfCloseRef.current) {
                markOfCloseRef.current.style.opacity = (0.36 - i * 0.06).toString();
              }
              //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
              await frameGate();
              await delay(200);
            }
            await profitEndAnimation();
            await delay(1000);
            setEndRecording(true);
          }
        }
      };
      func();
    }
  }, [chartFlow, simulationIsInProcess, greetingEnded]);

  //возврат всех состояний в исходное - подготовка к следующему видео
  useEffect(() => {
    if (endRecording) {
      // сообщаем в puppeteer об окончании цикла отрисовки кадров для видео:
      window.__captureStopFlag = true;
      if (typeof window.__puppeteerAllDone === "function") {
        window.__puppeteerAllDone();
      }
      markOfOpenPositionCountRef.current = 0;
      markOfAdd1PositionCountRef.current = 0;
      markOfAdd2PositionCountRef.current = 0;
      markOfClosePositionCountRef.current = 0;
      setEndRecording(false);
    }
  }, [endRecording]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <p className={styles.title}>{translate(language, "timeframe-" + timeframe)}</p>
        <p className={styles.profit} ref={profitRef}>
          {profit && (
            <>
              <span>{translate(language, "profit")}</span> <span>{profit}</span>
            </>
          )}
        </p>
        <div className={styles.name}>
          {/*<p className={styles.shortName}>{ticker} <span>{exchangeName}</span></p>*/}
          <p className={styles.shortName}>{ticker}</p>
          <p className={styles.fullName}>{companyName}</p>
        </div>
        <div className={styles.markWrap}>
          <div className={styles.markOfOpen} ref={markOfOpenRef}>
            <p>
              <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Buy" role="img">
                <title>Buy</title>
                <path d="M12 19V5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path
                  d="M5.5 11.5L12 5l6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </p>
          </div>
        </div>
        <div className={styles.markWrap}>
          <div className={styles.markOfAdd1} ref={markOfAdd1Ref}>
            <p>
              <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Buy" role="img">
                <title>Buy</title>
                <path d="M12 19V5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path
                  d="M5.5 11.5L12 5l6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </p>
          </div>
        </div>
        <div className={styles.markWrap}>
          <div className={styles.markOfAdd2} ref={markOfAdd2Ref}>
            <p>
              <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Buy" role="img">
                <title>Buy</title>
                <path d="M12 19V5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path
                  d="M5.5 11.5L12 5l6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </p>
          </div>
        </div>
        <div className={styles.markWrap}>
          <div className={styles.markOfClose} ref={markOfCloseRef}>
            <p>
              <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Sell" role="img">
                <title>Sell</title>
                <path d="M12 5v14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path
                  d="M5.5 12.5L12 19l6.5-6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </p>
          </div>
        </div>
        <div className={styles.canvasWrap}>
          <canvas ref={chartCanvas} width={canvasSize?.width} height={canvasSize?.height}>
            Ваш браузер не поддерживается!
          </canvas>
        </div>
      </div>
    </div>
  );
};

export default Chart;
